import angular from 'angular';
import PusherLibrary from 'pusher-js';
import 'pusher-js-auth';
import {findIndex} from 'underscore';

const TYPE_PUBLIC   = 'public';
const TYPE_PRIVATE  = 'private';
const TYPE_PRESENCE = 'presence';

const RESOURCE_USERS = 'users';

const PLATFORM_ALL     = 'all';
const PLATFORM_WEB_APP = 'web-app';

const JOINER_ID       = '=';
const JOINER_GROUP    = ',';
const JOINER_PLATFORM = '@';

const ENCODE_SIGNATURE = 'PS|';

// PusherLibrary.logToConsole = true;

/* @ngInject */
class Pusher {
	constructor(AuthService, $pusher) {
		this.AuthService   = AuthService;
		this.channels      = {};
		this.eventHandlers = [];

		this.callbacks = {
			onLogin:  () => {
				this.pusher.client.config.auth.headers.Authorization = `Bearer ${this.AuthService.getToken()}`;

				this.refreshSubscriptions();
			},
			onLogout: () => {
				if (this.pusher.client.config.auth.headers.hasOwnProperty('Authorization')) {
					delete this.pusher.client.config.auth.headers.Authorization;
				}

				this.refreshSubscriptions();
			},
		};

		const client = new PusherLibrary(process.env.PUSHER_KEY, {
			authDelay:     200,
			authEndpoint:  `${process.env.API_ENDPOINT}auth/pusher`,
			authTransport: 'buffered',
			cluster:       process.env.PUSHER_CLUSTER,
			encrypted:     true,
			auth:          {
				headers: {},
			},
		});

		this.pusher = $pusher(client);

		if (AuthService.isLoggedIn()) {
			this.pusher.client.config.auth.headers.Authorization = `Bearer ${this.AuthService.getToken()}`;
		}
	}

	static generateChannelName(type, resource, id, group, platform) {
		let channelName = '';

		switch (type) {
			case 'public': {
				break;
			}

			case 'private': {
				channelName += 'private-';

				break;
			}

			case 'presence': {
				channelName += 'presence-';

				break;
			}
		}

		channelName += resource;

		if (id) {
			channelName += `=${id}`;
		}

		if (group) {
			channelName += `,${group}`;
		}

		if (platform) {
			channelName += `@${platform}`;
		}

		return channelName;
	}

	static parseChannelName(channelName) {
		let type     = NULL;
		let resource = NULL;
		let id       = NULL;
		let group    = NULL;
		let platform = NULL;

		let resourceIndex = 0;

		if (channelName.indexOf(`${TYPE_PRIVATE}-`) === 0) {
			type = TYPE_PRIVATE;

			resourceIndex = `${TYPE_PRIVATE}-`.length;
		} else if (channelName.indexOf(`${TYPE_PRESENCE}-`) === 0) {
			type = TYPE_PRESENCE;

			resourceIndex = `${TYPE_PRESENCE}-`.length;
		} else {
			type = TYPE_PUBLIC;
		}

		let idIndex       = channelName.indexOf(JOINER_ID);
		let groupIndex    = channelName.indexOf(JOINER_GROUP);
		let platformIndex = channelName.indexOf(JOINER_PLATFORM);

		if (idIndex !== -1) {
			resource = channelName.substr(resourceIndex, idIndex - resourceIndex);
		} else if (groupIndex !== -1) {
			resource = channelName.substr(resourceIndex, groupIndex - resourceIndex);
		} else if (platformIndex !== -1) {
			resource = channelName.substr(resourceIndex, platformIndex - resourceIndex);
		} else {
			resource = channelName.substr(resourceIndex);
		}

		if (idIndex !== -1) {
			idIndex += JOINER_ID.length;

			if (groupIndex !== -1) {
				id = channelName.substr(idIndex, groupIndex - idIndex);
			} else if (platformIndex !== -1) {
				id = channelName.substr(idIndex, platformIndex - idIndex);
			} else {
				id = channelName.substr(idIndex);
			}
		}

		if (groupIndex !== -1) {
			groupIndex += JOINER_GROUP.length;

			if (platformIndex !== -1) {
				group = channelName.substr(groupIndex, platformIndex - groupIndex);
			} else {
				group = channelName.substr(groupIndex);
			}
		}

		if (platformIndex !== -1) {
			platformIndex += JOINER_PLATFORM.length;

			platform = channelName.substr(platformIndex);
		}

		return {
			type,
			resource,
			id,
			group,
			platform,
		};
	}

	init() {
		this.AuthService.onLogin(this.callbacks.onLogin);
		this.AuthService.onLogout(this.callbacks.onLogout);

		this.refreshSubscriptions();
	}

	subscribe(channelName, unsubscribeOnLogout) {
		if (!this.channels.hasOwnProperty(channelName)) {
			const instance = this.pusher.subscribe(channelName);

			this.channels[channelName] = {
				instance,
				handler: (event, data) => {
					this.eventHandler(channelName, event, data);
				},
				unsubscribeOnLogout,
			};

			instance.bind_all(this.channels[channelName].handler);
		}
	}

	unsubscribe(channelName) {
		if (this.channels.hasOwnProperty(channelName)) {
			this.channels[channelName].instance.unbind();

			this.pusher.unsubscribe(channelName);

			delete this.channels[channelName];
		}
	}

	refreshSubscriptions() {
		this.subscribe(Pusher.generateChannelName('public', 'users', null, null, 'all'), false);
		this.subscribe(Pusher.generateChannelName('public', 'users', null, null, 'web-app'), false);

		// Prune old channels
		if (!this.AuthService.isLoggedIn()) {
			angular.forEach(this.channels, (values, channelName) => {
				if (values.unsubscribeOnLogout) {
					this.unsubscribe(channelName);
				}
			});
		} else {
			this.subscribe(Pusher.generateChannelName('private', 'users', this.AuthService.getUser().id, null, 'all'), true);
			this.subscribe(Pusher.generateChannelName('private', 'users', this.AuthService.getUser().id, null, 'web-app'), true);

			this.subscribe(Pusher.generateChannelName('presence', 'users', null, null, 'all'), true);
			this.subscribe(Pusher.generateChannelName('presence', 'users', null, null, 'web-app'), true);

			if (this.AuthService.isStaff()) {
				this.subscribe(Pusher.generateChannelName('presence', 'users', null, 'staff', 'all'), true);
				this.subscribe(Pusher.generateChannelName('presence', 'users', null, 'staff', 'web-app'), true);
			} else if (this.AuthService.isSupportWorker()) {
				this.subscribe(Pusher.generateChannelName('presence', 'users', null, 'support-workers', 'all'), true);
				this.subscribe(Pusher.generateChannelName('presence', 'users', null, 'support-workers', 'web-app'), true);
			}
		}
	}

	on(channelName, event, callback) {
		this.eventHandlers.push({
			channelName,
			event,
			callback,
		});
	}

	off(channelName, event, callback) {
		const index = findIndex(this.eventHandlers, {
			channelName,
			event,
			callback,
		});

		if (index !== -1) {
			this.eventHandlers = this.eventHandlers.splice(index, 1);
		}
	}

	eventHandler(channelName, event, data) {
		angular.forEach(this.eventHandlers, (eventHandler) => {
			if (eventHandler.channelName === null || eventHandler.channelName === channelName) {
				if (eventHandler.event === null || eventHandler.event === event) {
					eventHandler.callback(channelName, event, data);
				}
			}
		});
	}
}

export default Pusher;


// WEBPACK FOOTER //
// ./src/js/services/pusher/index.js